import React, { useState, useMemo, useEffect } from 'react';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';
import { useHistory } from 'react-router-dom';

import { createLeadComment } from 'api/lead-record';
import { createProjectNote, saveProjectNote } from 'api/projects';
import { getProjectLimits } from 'containers/Projects/actions';
import DropdownButton from 'components/Button/DropdownButton/DropdownButton';
import APModal from 'components/Modal/APModal';
import APResponsiveDropdown from 'components/Dropdown/APResponsiveDropdown';
import { CreateCustomTemplateProject } from 'components/Modal/Project/CreateCustomTemplateProject';
import ProjectSettings from 'components/Modal/Project/ProjectSettings';
import ProjectsLimitModal from 'pages/Projects/Projects.LimitModal';
import { FILES, EMAILS, CHECKLIST } from './index';

/**
 *
 * @param {object} props
 * @param {number} props.orgId - the id of the organization
 * @param {object} props.lead - the lead object
 * @param {function(string): void} props.setTab - changes the tab of the lead record page
 * @param {function(): void} props.onMarkAsLost - callback to open the archive modal
 * @param {function(): void} props.onDeleteLead - callback to delete the lead
 * @param {number} props.projectCount - number of projects created for the org.
 * @param {function(): void} props.openFileModal - callback to open file modal.
 * @param {function(): void} props.openNewEmailModal - callback to open new email modal.
 * @returns JSX.React
 */
export const LeadRecordActionButton = ({
  orgId,
  lead,
  setTab,
  onMarkAsLost,
  onDeleteLead,
  projectCount = -1,
  openFileModal,
  addNewTask,
  openNewEmailModal,
}) => {
  const dispatch = useDispatch();
  const { id: leadId, projectType, notes } = lead || {};
  const projectsLimit = useSelector((state) => state.projects.projectsLimit);
  const userInfo = useSelector((state) => state.global.userInfo);
  const { personas, firstName, lastName } = userInfo || {};
  const { integratedStatus } = personas || {};

  const [showActionOptions, toggleActionDropdown] = useState(false);
  const [showProjectCreate, setShowProjectCreate] = useState(false);
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [finishedWeddingId, setFinishedWeddingId] = useState();
  const [isProjectLimitModalOpen, setIsProjectLimitModalOpen] = useState(false);

  const formData = useMemo(() => transformLeadToProjectData(lead), [lead]);

  const history = useHistory();

  const goToProject = (id) => {
    const _id = id ?? finishedWeddingId;
    window.location.href = `/app/project/${_id}/dashboard`;
  };

  const show = () => {
    toggleActionDropdown(!showActionOptions);
  };

  const dismiss = () => {
    toggleActionDropdown(false);
  };

  const handleCreateProject = () => {
    dismiss();
    if (!projectsLimit || projectsLimit.activeWeddingCount >= projectsLimit.limit) {
      setIsProjectLimitModalOpen(true);
      return;
    }

    setShowProjectCreate(true);
  };

  const projectCreateStarted = () => {
    setShowLoadingModal(true);
  };

  const projectCreated = async (data) => {
    const { createWedding } = data;
    const { wedding } = createWedding || {};
    const { id } = wedding || {};
    const currentUserName = [firstName, lastName].join(' ');
    const body = projectType === 1 ? 'created this wedding.' : 'created this project.';
    const comment = `${currentUserName} ${body}`;
    const kind = 'aisleplanner';
    await createLeadComment(orgId, lead.id, comment, kind);
    if (notes) {
      const newNote = await createProjectNote(id);
      const { data: newNoteData } = newNote || {};

      if (newNoteData) {
        const noteString = JSON.stringify(notes).replace(/\\n/g, '<br />').replace(/"/g, '');
        const parts = noteString.split('<br />');
        const paragraphs = parts.map(part => ({
          type: 'paragraph',
          children: [{ text: part }],
        }));
        const note = {
          ...newNoteData,
          _isNew: true,
          planner_only: true,
          note_html: `<p>${noteString}</p>`,
          note_json: { currentVersion: paragraphs },
        };
        await saveProjectNote(id, newNoteData.id, note);
      }
    }

    setFinishedWeddingId(id);
  };
  const projectCreatedError = () => {};

  // Fetch project limits
  useEffect(() => {
    dispatch(getProjectLimits());
  }, []);

  const renderActionMenu = () => {
    return (
      <>
        <button
          className={clsx(
            'action_buttons__button',
            'button--unstyled',
            'button--menu',
          )}
          onClick={() => {
            dismiss();
            addNewTask();
            setTab(CHECKLIST);
            history.push(`/app/v2/${orgId}/leads/lead/${leadId}/sales-workflow`);
          }}
        >
          Add New Workflow
        </button>
        {integratedStatus === 'connected' && (
          <>
            <button
              className={clsx(
                'action_buttons__button',
                'button--unstyled',
                'button--menu',
              )}
              onClick={() => {
                dismiss();
                setTab(EMAILS);
                history.push(`/app/v2/${orgId}/leads/lead/${leadId}/emails`);
                openNewEmailModal();
              }}
            >
              Create New Email
            </button>
            <button
              className={clsx(
                'action_buttons__button',
                'button--unstyled',
                'button--menu',
              )}
              onClick={() => {
                dismiss();
                setTab(EMAILS);
                history.push(`/app/v2/${orgId}/leads/lead/${leadId}/emails`);
              }}
            >
              View All Emails
            </button>
          </>
        )}
        <button
          className={clsx(
            'action_buttons__button',
            'button--unstyled',
            'button--menu',
          )}
          onClick={() => {
            dismiss();
            setTab(FILES);
            openFileModal();
            history.push(`/app/v2/${orgId}/leads/lead/${leadId}/files`);
          }}
        >
          Add New File
        </button>
        {projectCount === 0 && (
          <button
            className={clsx(
              'action_buttons__button',
              'button--unstyled',
              'button--menu',
            )}
            onClick={handleCreateProject}
          >
            Create Project
          </button>
        )}
        <button
          className={clsx(
            'action_buttons__button',
            'button--unstyled',
            'button--menu',
          )}
          onClick={() => {
            dismiss();
            onMarkAsLost();
          }}
        >
          Mark as Lost
        </button>
        {projectCount === 0 && (
          <button
            className={clsx(
              'action_buttons__button',
              'button--unstyled',
              'button--menu',
            )}
            onClick={() => {
              dismiss();
              onDeleteLead();
            }}
          >
            Delete Lead
          </button>
        )}
      </>
    );
  };

  const trigger = (
    <DropdownButton className="button button--md button--action">
      <span className="visually-hidden--mobile">Actions</span>
      {showActionOptions ? (
        <MdKeyboardArrowUp size={16} />
      ) : (
        <MdKeyboardArrowDown size={16} />
      )}
    </DropdownButton>
  );

  return (
    <>
      <APResponsiveDropdown
        opened={showActionOptions}
        show={show}
        dismiss={dismiss}
        triggerButton={trigger}
      >
        {renderActionMenu()}
      </APResponsiveDropdown>
      {lead && (
        <APModal
          open={showProjectCreate}
          title="Create a new project."
          name="create-project"
          className="create_quick_project_modal__container"
          onClose={() => setShowProjectCreate(false)}
        >
          <ProjectSettings
            type={projectType}
            formData={formData}
            projectCreateStartCallback={projectCreateStarted}
            projectCreatedCallback={projectCreated}
            projectCreatedErrorCallback={projectCreatedError}
          />
        </APModal>

      )}
      {showLoadingModal && (
        <CreateCustomTemplateProject
          show={showLoadingModal}
          goToProject={goToProject}
          projectId={finishedWeddingId}
        />
      )}
      {isProjectLimitModalOpen && (
        <ProjectsLimitModal
          setIsOpen={setIsProjectLimitModalOpen}
          projectsLimit={projectsLimit}
        />
      )}
    </>
  );
};

const transformLeadToProjectData = (lead) => {
  if (!lead) return;
  const { id, projectName, estimatedBudget, ...rest } = lead;
  return {
    ...rest,
    name: projectName,
    // Project settings forms expects dollar amount.
    estimatedBudget: estimatedBudget / 100,
    leadId: id,
  };
};
